/**
 * Catalog Admin API client (BON-161). Typed wrappers over
 * /api/v1/inventory/catalog/*. Mirrors the backend service's return shapes.
 */
import { apiFetch } from '../lib/apiFetch';

// ---------------------------------------------------------------------------
// Types (mirror inventory-catalog.types.ts on the backend)
// ---------------------------------------------------------------------------

export type AttributeValues = Record<string, unknown>;

export interface CatalogItem {
  id: string;
  name: string;
  sku: string;
  category: string;
  attributes: AttributeValues;
  isConsumable: boolean;
  isActive: boolean;
  mergedIntoId: string | null;
  mergedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CatalogItemEditEvent {
  id: string;
  eventType: string;
  actorName: string;
  actorRole: string;
  fieldChanges: Record<string, unknown>;
  changedAt: string;
}

export interface CatalogItemDetail extends CatalogItem {
  editEvents: CatalogItemEditEvent[];
}

export interface ListItemsResult {
  items: CatalogItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AttributeDefinition {
  id: string;
  category: string;
  name: string;
  dataType: string;
  allowedValues: unknown;
  isRequired: boolean;
  displayOrder: number;
  description: string | null;
  isActive: boolean;
}

export interface ItemRequest {
  id: string;
  requestedByName: string;
  category: string;
  proposedName: string;
  proposedAttributes: AttributeValues;
  requestNotes: string | null;
  status: string;
  reviewNotes: string | null;
  createdItemId: string | null;
  createdAt: string;
}

export interface ListRequestsResult {
  requests: ItemRequest[];
  total: number;
  page: number;
  pageSize: number;
}

export const CATALOG_CATEGORIES = [
  'tree',
  'pot',
  'tray',
  'kit',
  'wire',
  'media',
  'supply',
] as const;
export type CatalogCategory = (typeof CATALOG_CATEGORIES)[number];

const BASE = '/api/v1/inventory/catalog';

async function readError(res: Response, fallback: string): Promise<never> {
  const err = (await res.json().catch(() => ({}))) as { message?: string };
  throw new Error(err.message ?? `${fallback}: ${res.status}`);
}

// ---------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------

export interface ListItemsParams {
  category?: string;
  q?: string;
  includeMerged?: boolean;
  activeOnly?: boolean;
  page?: number;
  pageSize?: number;
}

export async function fetchCatalogItems(params: ListItemsParams = {}): Promise<ListItemsResult> {
  const qs = new URLSearchParams();
  if (params.category) qs.set('category', params.category);
  if (params.q) qs.set('q', params.q);
  if (params.includeMerged) qs.set('includeMerged', 'true');
  if (params.activeOnly) qs.set('activeOnly', 'true');
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  const query = qs.toString();
  const res = await apiFetch(`${BASE}/items${query ? `?${query}` : ''}`);
  if (!res.ok) await readError(res, 'Failed to load items');
  return res.json() as Promise<ListItemsResult>;
}

export async function fetchCatalogItem(id: string): Promise<CatalogItemDetail> {
  const res = await apiFetch(`${BASE}/items/${id}`);
  if (!res.ok) await readError(res, 'Failed to load item');
  return res.json() as Promise<CatalogItemDetail>;
}

export interface CreateItemBody {
  category: string;
  name: string;
  sku?: string;
  attributes?: AttributeValues;
  isConsumable?: boolean;
}

export async function createCatalogItem(body: CreateItemBody): Promise<CatalogItem> {
  const res = await apiFetch(`${BASE}/items`, { method: 'POST', body: JSON.stringify(body) });
  if (!res.ok) await readError(res, 'Failed to create item');
  return res.json() as Promise<CatalogItem>;
}

export type UpdateItemBody = Partial<Omit<CreateItemBody, 'category'>> & { isActive?: boolean };

export async function updateCatalogItem(id: string, body: UpdateItemBody): Promise<CatalogItem> {
  const res = await apiFetch(`${BASE}/items/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  if (!res.ok) await readError(res, 'Failed to update item');
  return res.json() as Promise<CatalogItem>;
}

export async function mergeCatalogItem(
  dissolvedId: string,
  survivingItemId: string,
): Promise<CatalogItem> {
  const res = await apiFetch(`${BASE}/items/${dissolvedId}/merge`, {
    method: 'POST',
    body: JSON.stringify({ survivingItemId }),
  });
  if (!res.ok) await readError(res, 'Failed to merge items');
  return res.json() as Promise<CatalogItem>;
}

export async function unmergeCatalogItem(id: string): Promise<CatalogItem> {
  const res = await apiFetch(`${BASE}/items/${id}/unmerge`, { method: 'POST', body: '{}' });
  if (!res.ok) await readError(res, 'Failed to unmerge item');
  return res.json() as Promise<CatalogItem>;
}

// ---------------------------------------------------------------------------
// Attribute definitions
// ---------------------------------------------------------------------------

export async function fetchAttributeDefinitions(category: string): Promise<AttributeDefinition[]> {
  const res = await apiFetch(`${BASE}/categories/${category}/attributes`);
  if (!res.ok) await readError(res, 'Failed to load attribute definitions');
  const data = (await res.json()) as { definitions: AttributeDefinition[] };
  return data.definitions;
}

export interface AddAttributeDefinitionBody {
  name: string;
  dataType: string;
  allowedValues?: string[];
  isRequired?: boolean;
  displayOrder?: number;
  description?: string | null;
  forceCreate?: boolean;
}

export type AddAttributeDefinitionResult =
  | { created: true; definition: AttributeDefinition }
  | { created: false; existingViolationsCount: number };

export async function addAttributeDefinition(
  category: string,
  body: AddAttributeDefinitionBody,
): Promise<AddAttributeDefinitionResult> {
  const res = await apiFetch(`${BASE}/categories/${category}/attributes`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (res.status === 409) {
    const data = (await res.json().catch(() => ({}))) as { existingViolationsCount?: number };
    if (typeof data.existingViolationsCount === 'number') {
      return { created: false, existingViolationsCount: data.existingViolationsCount };
    }
  }
  if (!res.ok) await readError(res, 'Failed to add attribute');
  return { created: true, definition: (await res.json()) as AttributeDefinition };
}

export async function updateAttributeDefinition(
  id: string,
  body: Partial<AddAttributeDefinitionBody>,
): Promise<AttributeDefinition> {
  const res = await apiFetch(`${BASE}/attributes/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  if (!res.ok) await readError(res, 'Failed to update attribute');
  return res.json() as Promise<AttributeDefinition>;
}

export async function disableAttributeDefinition(id: string): Promise<AttributeDefinition> {
  const res = await apiFetch(`${BASE}/attributes/${id}`, { method: 'DELETE' });
  if (!res.ok) await readError(res, 'Failed to disable attribute');
  return res.json() as Promise<AttributeDefinition>;
}

export async function clearAttributeFromItems(id: string): Promise<{ cleared: number }> {
  const res = await apiFetch(`${BASE}/attributes/${id}/clear-from-items`, {
    method: 'POST',
    body: '{}',
  });
  if (!res.ok) await readError(res, 'Failed to clear attribute');
  return res.json() as Promise<{ cleared: number }>;
}

// ---------------------------------------------------------------------------
// Item requests
// ---------------------------------------------------------------------------

export async function fetchItemRequests(status?: string): Promise<ListRequestsResult> {
  const res = await apiFetch(`${BASE}/requests${status ? `?status=${status}` : ''}`);
  if (!res.ok) await readError(res, 'Failed to load requests');
  return res.json() as Promise<ListRequestsResult>;
}

export async function fetchMyItemRequests(): Promise<ItemRequest[]> {
  const res = await apiFetch(`${BASE}/requests/mine`);
  if (!res.ok) await readError(res, 'Failed to load your requests');
  const data = (await res.json()) as { requests: ItemRequest[] };
  return data.requests;
}

export interface SubmitRequestBody {
  category: string;
  proposedName: string;
  proposedAttributes?: AttributeValues;
  requestNotes?: string | null;
}

export async function submitItemRequest(body: SubmitRequestBody): Promise<ItemRequest> {
  const res = await apiFetch(`${BASE}/requests`, { method: 'POST', body: JSON.stringify(body) });
  if (!res.ok) await readError(res, 'Failed to submit request');
  return res.json() as Promise<ItemRequest>;
}

export interface ApproveRequestBody {
  name?: string;
  sku?: string;
  attributes?: AttributeValues;
  isConsumable?: boolean;
}

export async function approveItemRequest(
  id: string,
  body: ApproveRequestBody = {},
): Promise<{ request: ItemRequest; item: CatalogItem }> {
  const res = await apiFetch(`${BASE}/requests/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) await readError(res, 'Failed to approve request');
  return res.json() as Promise<{ request: ItemRequest; item: CatalogItem }>;
}

export async function rejectItemRequest(id: string, reason: string): Promise<ItemRequest> {
  const res = await apiFetch(`${BASE}/requests/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
  if (!res.ok) await readError(res, 'Failed to reject request');
  return res.json() as Promise<ItemRequest>;
}
